import { notNil } from './nil'

export const isObject = value =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

export const pick = (arr, obj) =>
  arr.reduce((acc, it) => (it in obj ? { ...acc, [it]: obj[it] } : acc), {})

export const omit = (arr, obj) =>
  Object.entries(obj)
    .filter(([a, b]) => !arr.includes(a))
    .reduce((acc, [key, val]) => ({ ...acc, [key]: val }), {})

export const path = (arr, obj, def = undefined) =>
  arr.reduce((acc, it) => (acc !== def && {}.hasOwnProperty.call(acc, it) ? acc[it] : def), obj)

export const isEmpty = (val = null) => val === null || !(Object.keys(val) || val).length

export const nonEmpty = (o = {}, def = undefined) => {
  const res = Object.entries(o)
    .filter(([, val]) => notNil(val))
    .reduce((acc, [key, val]) => ({ ...acc, [key]: val }), {})

  return isEmpty(res) ? def : res
}

/**
 * Map over an objects entries
 *
 * @param {Function} f - Function to apply to tuples of Key/Values
 * @param {Object} obj - The object to map over
 */
export const mapObj = (f = i => i, obj) =>
  Object.entries(obj).reduce((acc, [key, val]) => ({ ...acc, [key]: f(val) }), {})

/**
 * Chunks an array into smaller arrays of a specified size.
 *
 * Use Array.from() to create a new array, that fits the number of
 * chunks that will be produced. Use Array.prototype.slice() to map
 * each element of the new array to a chunk the length of size. If the
 * original array can't be split evenly, the final chunk will contain
 * the remaining elements.
 *
 * @param {Array} arr - List of items to chunk
 * @param {number} size - The size of each chunk
 * @returns {Array}
 */
export const chunk = (arr, size) =>
  Array.from({ length: Math.ceil(arr.length / size) }, (v, i) =>
    arr.slice(i * size, i * size + size),
  )

export const compose = (...fns) => fns.reduce((f, g) => (...args) => f(g(...args)))

export const unpick = (arr, obj) =>
  Object.entries(obj).reduce(
    (acc, [key, val]) => (arr.includes(key) ? acc : { ...acc, [key]: val }),
    {},
  )

/**
 * Takes an object with arrays as properties, and returns an array of objects
 * with the same keys, but with values so that every possible permutation of
 * properties is covered.
 *
 * Example
 * For {foo: [1, 2], bar: [3, 4]}, this function returns:
 * [{foo: 1, bar 3}, {foo: 1, bar: 4}, {foo: 2, bar: 3}, {foo: 2, bar: 4}]
 *
 * @param {Object} permutationsObject
 * @returns {Array} - each element is a permutation
 */
export const permutationsGenerator = permutationsObject => {
  if (!Object.keys(permutationsObject).length) {
    return [{}]
  }
  const permutationsArray = []
  const [objectKey, objectValues] = Object.entries(permutationsObject)[0]
  const omittedObject = { ...permutationsObject }
  delete omittedObject[objectKey]
  const recursiveValues = permutationsGenerator(omittedObject)
  objectValues.forEach(value => {
    recursiveValues.forEach(values => {
      permutationsArray.push({
        [objectKey]: value,
        ...values,
      })
    })
  })
  return permutationsArray
}
